import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [{ d: 7, l: '7 días' }, { d: 30, l: 'Último mes' }, { d: 90, l: '3 meses' }];
const fmtDate = (iso) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
const n = (v) => Number.isInteger(v) ? v : Number(v).toFixed(2);

function RefDetail({ refInfo, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!refInfo) return;
    setData(null);
    axios.get(`${API}/stock-history-ref/${refInfo.ref_type || 'any'}/${refInfo.ref_id}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'No se pudo abrir el movimiento'); onClose(); });
  }, [refInfo, onClose]);
  return (
    <Dialog open={!!refInfo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="stock-ref-dialog">
        <DialogHeader><DialogTitle>{data ? `${data.title}` : 'Cargando…'}</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-3 text-sm">
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
              {data.fields.map(([k, v]) => (
                <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium break-words">{v}</dd></div>
              ))}
            </dl>
            {data.items.length > 0 && (
              <div className="border rounded divide-y">
                {data.items.map((it, i) => (
                  <div key={i} className="flex justify-between gap-2 px-2 py-1.5 text-xs">
                    <span className="truncate">{it.name}</span>
                    <span className="font-semibold whitespace-nowrap">
                      {it.quantity} pzas{it.received_qty != null ? ` · recibidas ${it.received_qty}` : ''}{it.unit_price ? ` · $${Number(it.unit_price).toFixed(2)}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const StockHistoryDialog = ({ product, onClose }) => {
  const [days, setDays] = useState(30);
  const [range, setRange] = useState({ start: '', end: '' });
  const [applied, setApplied] = useState(null);
  const [data, setData] = useState(null);
  const [refInfo, setRefInfo] = useState(null);

  useEffect(() => {
    if (!product) return;
    setData(null);
    const q = applied ? `start=${applied.start}&end=${applied.end}` : `days=${days}`;
    axios.get(`${API}/stock-history/${product.id}?${q}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Error al cargar historial'));
  }, [product, days, applied]);

  const applyRange = () => {
    if (!range.start || !range.end) return toast.error('Elige fecha inicial y final');
    if (range.start > range.end) return toast.error('La fecha inicial debe ser antes de la final');
    setApplied({ ...range });
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="stock-history-dialog">
        <DialogHeader>
          <DialogTitle>📜 Historial de stock · {product?.name}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {RANGES.map(r => (
              <button key={r.d} onClick={() => { setApplied(null); setDays(r.d); }} data-testid={`stock-history-range-${r.d}`}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${!applied && days === r.d ? 'bg-white shadow font-semibold text-sky-700' : 'text-slate-500'}`}>{r.l}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-slate-500">Desde</span>
            <input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} className="border rounded px-2 py-1" data-testid="stock-history-start" />
            <span className="text-slate-500">hasta</span>
            <input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} className="border rounded px-2 py-1" data-testid="stock-history-end" />
            <button onClick={applyRange} className={`px-3 py-1 rounded-md font-semibold ${applied ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-700 hover:bg-sky-100'}`} data-testid="stock-history-apply-range">Ver rango</button>
          </div>
          {data && <span className="text-slate-600">{data.branch_name} · Stock actual: <b data-testid="stock-history-current">{n(data.current_stock)}</b></span>}
        </div>
        {!data ? <p className="text-sm text-muted-foreground py-6 text-center">Cargando…</p>
          : data.movements.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Sin movimientos en este periodo</p>
          : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase">
                  <tr>
                    <th className="px-2 py-2 text-left">Fecha</th><th className="px-2 py-2 text-left">Movimiento</th>
                    <th className="px-2 py-2 text-right">Cantidad</th><th className="px-2 py-2 text-right">Antes</th>
                    <th className="px-2 py-2 text-right">Después</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.movements.map(m => (
                    <tr key={m.id} data-testid={`stock-mov-${m.id}`}
                      onDoubleClick={() => (m.ref_id ? setRefInfo(m) : toast.info('Este movimiento no tiene un registro ligado'))}
                      className={`hover:bg-sky-50 ${m.ref_id ? 'cursor-pointer' : ''}`}
                      title={m.ref_id ? 'Doble clic para ver el detalle' : ''}>
                      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(m.at)}</td>
                      <td className="px-2 py-1.5">
                        <div className="font-medium">{m.label}{m.folio ? ` · ${m.folio}` : ''}</div>
                        {m.notes && <div className="text-[11px] text-slate-500">{m.notes}</div>}
                      </td>
                      <td className={`px-2 py-1.5 text-right font-bold ${m.delta < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{m.delta > 0 ? '+' : ''}{n(m.delta)}</td>
                      <td className="px-2 py-1.5 text-right">{m.approx ? '≈' : ''}{n(m.before)}</td>
                      <td className="px-2 py-1.5 text-right font-semibold">{m.approx ? '≈' : ''}{n(m.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        <p className="text-[11px] text-muted-foreground">Doble clic en un movimiento para abrir su detalle. "≈" = movimiento anterior al registro exacto (calculado).</p>
        <RefDetail refInfo={refInfo} onClose={() => setRefInfo(null)} />
      </DialogContent>
    </Dialog>
  );
};
